// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { QtyFieldStyles } from "@sc/App/Components/Qty/QtyField";
import { xIsAuthenticated } from "@sc/App/Store/Selectors/GeneralSelectors";
import ProductAddToCartButton from "@sc/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, { ProductQuantityStyles } from "@sc/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@sc/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    secure?: boolean;
    itemListName?: string;
}

type Props = ProductActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: InjectableCss;
    productQuantityOrderedStyles?: QtyFieldStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToCartButtonWrapper?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productUomWrapper?: InjectableCss;
}

export const productActionsStyle: ProductActionsStyle = {};

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
});
const ProductActions: FC<Props> = ({
    carouselId,
    extendedStyles,
    onAddToCartClick,
    isAuthenticated,
    secure,
    productContext: {
        product: { unitOfMeasures },
    },
    itemListName,
}) => {
    if (secure && !isAuthenticated) {
        return null;
    }
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    showDisabled={true}
                    extendedStyles={styles.productQuantityOrderedStyles!}
                    data-test-selector="productQty"
                />
            </StyledWrapper>

            {unitOfMeasures && (
                <StyledWrapper {...styles.productUomWrapper}>
                    <ProductUnitOfMeasureSelect
                        labelOverride=""
                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                        id={`${carouselId}-uom`}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <ProductAddToCartButton
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                    itemListName={itemListName}
                    {...{
                        showDisabled: true,
                    }}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductActions));
